/** F5 sidebar + F6 pagina de secao. */
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
// import nomeado, nao `import *`: com namespace o bundler nao consegue
// descartar os mais de mil icones que nao usamos.
import {
  Circle, FilePen, GraduationCap, IdCard, Inbox, LayoutDashboard, ListChecks, LogOut, Menu, Moon,
  PanelLeftClose, PanelLeftOpen, Plug, Settings, Shield, Sun, Target, TriangleAlert, UserRound,
  X, type LucideIcon,
} from 'lucide-react';
import { useIdioma } from '../i18n/index.tsx';
import { useSessao, useTema } from '../sessao.tsx';
import { menuVisivel } from './menu.ts';
import { guardarSidebarRecolhida, sidebarRecolhida } from './sidebar.ts';
import './shell.css';

const ICONES: Record<string, LucideIcon> = {
  LayoutDashboard, Target, ListChecks, GraduationCap, IdCard, FilePen, Plug, Settings, Shield,
  Menu, X, Sun, Moon, LogOut, Inbox, TriangleAlert, PanelLeftClose, PanelLeftOpen, UserRound,
};

function Icone({ nome }: { nome: string }) {
  // icone SVG de linha (Lucide), nunca emoji
  const C = ICONES[nome] ?? Circle;
  return <C size={17} strokeWidth={1.75} />;
}

export function Shell() {
  const { t } = useIdioma();
  const { estado, sair } = useSessao();
  const { tema, trocar: trocarTema } = useTema();
  const [aberta, setAberta] = useState(false);
  // so no desktop: abaixo de 860px vale o hamburguer e `aberta`, como antes
  const [recolhida, setRecolhida] = useState(sidebarRecolhida);
  const alternarSidebar = () => {
    const nova = !recolhida;
    setRecolhida(nova);
    guardarSidebarRecolhida(nova);
  };

  if (estado.fase !== 'dentro') return null;
  const itens = menuVisivel(estado.eu.modulos, estado.eu.permissoes);

  return (
    <div className={`shell${recolhida ? ' shell--recolhida' : ''}`}>
      <button
        className="shell__hamburguer" type="button"
        aria-label="Menu" aria-expanded={aberta}
        onClick={() => setAberta((v) => !v)}
      >
        <Icone nome={aberta ? 'X' : 'Menu'} />
      </button>

      <nav className={`sidebar${aberta ? ' sidebar--aberta' : ''}`}>
        <div className="sidebar__topo">
          {/* o simbolo oficial num selo branco: o logo nao e redesenhado nem recolorido */}
          <span className="sidebar__logo">
            <img src="/marca/simbolo-tme.png" alt="" width={26} height={26} />
          </span>
          <div className="sidebar__ws">
            <span className="sidebar__ws-nome">{t('app.nome')}</span>
            <span className="sidebar__ws-rotulo">{t('app.subtitulo')}</span>
          </div>
          <button
            className="sidebar__botao sidebar__recolher" type="button"
            aria-expanded={!recolhida}
            aria-label={t(recolhida ? 'shell.expandir' : 'shell.recolher')}
            title={t(recolhida ? 'shell.expandir' : 'shell.recolher')}
            onClick={alternarSidebar}
          >
            <Icone nome={recolhida ? 'PanelLeftOpen' : 'PanelLeftClose'} />
          </button>
        </div>

        <ul className="sidebar__lista">
          {itens.map((i) => (
            <li key={i.id}>
              <NavLink
                to={i.rota} end={i.rota === '/'}
                className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--ativo' : ''}`}
                onClick={() => setAberta(false)}
                // recolhida, so o icone aparece: o nome vai no title e no aria-label
                {...(recolhida ? { title: t(i.rotulo), 'aria-label': t(i.rotulo) } : {})}
              >
                <Icone nome={i.icone} />
                <span>{t(i.rotulo)}</span>
              </NavLink>
            </li>
          ))}
          {/* Minha conta: trocar a propria senha */}
          <li>
            <NavLink
              to="/conta"
              className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--ativo' : ''}`}
              onClick={() => setAberta(false)}
              {...(recolhida ? { title: t('shell.conta'), 'aria-label': t('shell.conta') } : {})}
            >
              <Icone nome="UserRound" />
              <span>{t('shell.conta')}</span>
            </NavLink>
          </li>
        </ul>

        <div className="sidebar__pe">
          <div className="sidebar__usuario">
            <span className="sidebar__usuario-nome">{estado.eu.usuario.nome}</span>
          </div>
          <div className="sidebar__controles">
            <button className="sidebar__botao" type="button" onClick={trocarTema}
              title={t('shell.tema')}>
              <Icone nome={tema === 'escuro' ? 'Sun' : 'Moon'} />
            </button>
            <button className="sidebar__botao" type="button" onClick={() => void sair()}
              title={t('shell.sair')}>
              <Icone nome="LogOut" />
            </button>
          </div>
        </div>
      </nav>

      <main className="conteudo"><Outlet /></main>
    </div>
  );
}

/** F6 - kicker, titulo, botao primario e abas da secao. */
export function PaginaSecao({
  kicker, titulo, acao, abas, abaAtiva, onAba, children,
}: {
  kicker: string;
  titulo: string;
  acao?: { rotulo: string; onClick: () => void };
  abas?: readonly { id: string; rotulo: string }[];
  abaAtiva?: string;
  onAba?: (id: string) => void;
  children?: React.ReactNode;
}) {
  return (
    <section className="secao">
      <header className="secao__cabeca">
        <div>
          <span className="kicker secao__kicker">{kicker}</span>
          <h1 className="secao__titulo">{titulo}</h1>
        </div>
        {acao !== undefined && (
          <button className="botao botao--primario" type="button" onClick={acao.onClick}>
            {acao.rotulo}
          </button>
        )}
      </header>

      {abas !== undefined && abas.length > 0 && (
        <div className="abas" role="tablist">
          {abas.map((a) => (
            <button
              key={a.id} type="button" role="tab"
              aria-selected={a.id === abaAtiva}
              className={`abas__item${a.id === abaAtiva ? ' abas__item--ativa' : ''}`}
              onClick={() => onAba?.(a.id)}
            >{a.rotulo}</button>
          ))}
        </div>
      )}

      <div className="secao__corpo">{children}</div>
    </section>
  );
}

/** Estados exigidos em toda tela: vazio, carregando e erro. */
export function EstadoVazio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="estado">
      <Icone nome="Inbox" />
      <h2 className="estado__titulo">{titulo}</h2>
      <p className="estado__texto">{texto}</p>
    </div>
  );
}

export function EstadoCarregando({ texto }: { texto: string }) {
  return <div className="estado" aria-busy="true"><p className="estado__texto">{texto}</p></div>;
}

export function EstadoErro({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="estado estado--erro" role="alert">
      <Icone nome="TriangleAlert" />
      <h2 className="estado__titulo">{titulo}</h2>
      <p className="estado__texto">{texto}</p>
    </div>
  );
}
